import { waitForTime } from './GeneralUtils';

export interface IGenericPollerReturnType {
  cancel: () => void;
  isRunning: boolean;
  run: () => Promise<void>;
  timeoutId: number | null;
}

export interface IGenericPollerParams<T> {
  /**
   * @description
   * To delay running poller for first attempt
   */
  delayFirstAttempt: boolean;

  /**
   * @description
   * Number in mili-seconds
   */
  interval: number;

  /**
   * @description
   * Number in tries
   */
  numberOfTries: number;

  /**
   * @description
   * Callback to run when polling is failed
   */
  onFailed: () => void;

  /**
   * @description
   * Callback to run when polling is succesfull
   */
  onSuccess: (response: T) => void;

  /**
   * @description
   * Polling function to watch polling
   */
  pollingFunction: (currentTry?: number) => Promise<null | false | T | undefined>;
}

export const genericPoller = <T>(params: IGenericPollerParams<T>): IGenericPollerReturnType => {
  const { interval, pollingFunction, delayFirstAttempt, numberOfTries, onSuccess, onFailed } = params;
  return {
    timeoutId: null,
    isRunning: false,
    run: async function () {
      let currentTry = 1;

      this.isRunning = true;

      if (delayFirstAttempt) {
        await waitForTime(interval).promise();
      }

      while (currentTry <= numberOfTries && this.isRunning) {
        try {
          const result = await pollingFunction(currentTry);

          if (result) {
            onSuccess(result);
            this.isRunning = false;
            break; // Break early if successful
          }

          if (currentTry + 1 > numberOfTries) {
            onFailed();
            break;
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
        const { promise, timeoutId } = waitForTime(interval);
        this.timeoutId = timeoutId;
        await promise();

        currentTry += 1;
      }

      this.isRunning = false;
    },
    cancel: function () {
      this.isRunning = false;
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
    },
  };
};
